import { useState } from "react";
import { Card, CardContent } from "@/shared/ui/card";
import { Textarea } from "@/shared/ui/textarea";
import type { QueueItem, SubmitBody, SubmitResult, Variant } from "../types";
import { AnswerControls } from "./AnswerControls";
import { EvidenceDisplay } from "./EvidenceDisplay";
import { OurClassification } from "./OurClassification";
import { ScratchControls } from "./ScratchControls";
import { VerdictControls } from "./VerdictControls";
import { RewardReveal } from "./RewardReveal";

/**
 * One labeling card: captured media on top, then the variant-specific controls.
 * The sample may be a flagged finding OR a clean image we didn't flag, so "no
 * deficiency" is a first-class answer. Variant A shows our assessment (including
 * "no deficiency") and asks the client to react; Variant B (default) hides it
 * and asks the client to classify from scratch, revealing ours after.
 */
export function FindingCard({
  item,
  variant,
  onSubmit,
  onNext,
}: {
  item: QueueItem;
  variant: Variant;
  onSubmit: (body: SubmitBody) => Promise<SubmitResult>;
  onNext: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<SubmitResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState("");

  const submit = async (partial: Partial<SubmitBody>) => {
    setBusy(true);
    setError(null);
    // Controls may pass a structured reason prefix (e.g. the tag-photo skip);
    // combine it with whatever the user typed instead of dropping either.
    const mergedReason = [partial.reason, reason.trim()].filter(Boolean).join(" ") || undefined;
    try {
      const r = await onSubmit({
        sampleKey: item.id,
        sampleKind: item.sample_kind,
        propertyId: item.property_id,
        assetId: item.asset_id,
        surveyFieldId: item.survey_field_id,
        documentId: item.document_id ?? null,
        variant,
        ourAnswerShown: variant === "A",
        ...partial,
        reason: mergedReason,
      } as SubmitBody);
      setResult(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardContent className="space-y-4 p-4">
        <EvidenceDisplay items={item.evidence} propertyId={item.property_id} />
        <p className="text-base font-medium text-foreground">{item.context}</p>

        {result ? (
          <RewardReveal item={item} result={result} revealOur={variant === "B"} onNext={onNext} />
        ) : (
          <>
            {variant === "A" ? (
              <>
                <OurClassification item={item} />
                <VerdictControls busy={busy} ourIsDeficiency={item.our_is_deficiency} onSubmit={submit} />
              </>
            ) : item.question ? (
              // Derivable survey question: answer in its own vocabulary; the
              // server derives severity from the answer via the rule pack.
              <AnswerControls question={item.question} busy={busy} onSubmit={submit} />
            ) : (
              <>
                <p className="text-sm text-muted-foreground">Based on the evidence above, what's your call?</p>
                <ScratchControls busy={busy} onSubmit={submit} />
              </>
            )}
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={busy}
              maxLength={4000}
              rows={2}
              placeholder="Add details (optional) — e.g. what you see, why it matters…"
              className="text-sm"
            />
          </>
        )}

        {error && <p className="text-sm text-destructive">{error}</p>}
      </CardContent>
    </Card>
  );
}
